import { Component, OnInit, OnDestroy } from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import { Subject } from 'rxjs';
import { filter, takeUntil } from 'rxjs/operators';
import { NgxSpinnerService } from 'ngx-spinner';
import { AuthService, ResponseObject } from '../../Services/auth.service';
import { SELLING_HUB, WEBSITE_HUB, SOCIAL_ACCOUNTS_HUB, LINK_SOCIALS_HUB, GALLERY_SELLING_HUB, GALLERY_WEBSITE_HUB, hubMatchPrefixes } from './section-hub/section-hub.config';

type ShellRole = 'artist' | 'gallery' | 'admin';

/**
 * New navigation shell (Phase 2, artist).
 *
 * Single consolidated navigation modelled on Canva's two-tier menu: a left icon
 * RAIL of the artist's content areas; clicking a rail item with sub-items opens a
 * secondary DRAWER listing them, and the content area shifts to make room. Rail
 * items with no sub-items (Home, Messages) navigate straight to their route. A top
 * bar (logo + utilities + profile) sits above, and a bottom bar (5 + More) replaces
 * the rail on tablet/phone — where a sectioned item opens the same drawer as an
 * overlay. Wraps the page via <ng-content> (app.component keeps <router-outlet>
 * inside it). Gated to regular artists via app.component's useNewArtistShell.
 *
 * The nav tree mirrors the artist directory in all-pages.component.ts (buildArtist)
 * — the validated route inventory — so it stays in sync with NAVIGATION-INVENTORY.md.
 */
interface NavChild { label: string; link: any[]; }
interface NavItem {
    label: string;
    icon: string;
    /** Default route the rail item navigates to on click (always loads content, Canva-style). */
    link?: any[];
    /** Secondary-drawer sub-items. When present, clicking the rail item ALSO opens the drawer. */
    children?: NavChild[];
    /** Extra route-path prefixes this item "owns" for active-state (used by hub sections). */
    match?: string[];
}

@Component({
    selector: 'ds-shell',
    templateUrl: './ds-shell.component.html',
    styleUrls: ['./ds-shell.component.css'],
    standalone: false
})
export class DsShellComponent implements OnInit, OnDestroy {
    public firstName = 'Artist';
    public avatarUrl: string | null = null;
    public isUserMenuOpen = false;

    /** Which role's shell this is — drives the nav tree + top-bar links. */
    public role: ShellRole = 'artist';
    public homeLink: any[] = ['/ds-dashboard'];
    public createLink: any[] | null = ['/artists', 'artworks', 'create'];
    public logoSrc = 'assets/images/AFP-logo-horizontal/AFP-logo-pro-artists-300.png';
    /** True when an admin is impersonating this role — shows a "Back to Admin" exit. */
    public viewingRoleAsAdmin = false;

    /** The rail section whose drawer is currently open (null = no drawer). */
    public openSection: NavItem | null = null;

    public navItems: NavItem[] = [];
    /** On a phone the bottom bar shows these five + a "More" that opens the directory. */
    public phonePrimary: string[] = ['Home', 'Artworks', 'Website', 'Events', 'Messages'];

    private destroy$ = new Subject<void>();

    constructor(private authService: AuthService, private router: Router, private spinner: NgxSpinnerService) { }

    public ngOnInit(): void {
        const u: any = this.authService.getCurrentUser();
        this.firstName = u?.FirstName || 'Artist';
        this.avatarUrl = (u?.LogoUrl && String(u.LogoUrl).trim()) ? u.LogoUrl : null;

        this.role = this.resolveRole();
        this.viewingRoleAsAdmin = !!(this.authService.isViewingAsAdministrator && this.authService.isViewingAsAdministrator());
        const user = this.authService.getUserName() || this.authService.getUserId();
        this.navItems = this.buildNav(this.role, user);
        this.applyRoleChrome();

        // Reflect the current route in the drawer (open the owning section), and keep
        // it in sync as the user navigates.
        this.syncSectionToUrl(this.router.url);
        this.router.events.pipe(
            filter((e): e is NavigationEnd => e instanceof NavigationEnd),
            takeUntil(this.destroy$)
        ).subscribe(e => this.syncSectionToUrl(e.urlAfterRedirects));
    }

    public ngOnDestroy(): void {
        this.destroy$.next();
        this.destroy$.complete();
    }

    private resolveRole(): ShellRole {
        if (this.authService.isGallery && this.authService.isGallery()) { return 'gallery'; }
        if ((this.authService.isAdministrator && this.authService.isAdministrator()) ||
            (this.authService.IsSuperAdmin && this.authService.IsSuperAdmin())) { return 'admin'; }
        return 'artist';
    }

    /** Role-specific top-bar links + phone bottom-bar primaries. */
    private applyRoleChrome(): void {
        const genericLogo = 'assets/images/thumb_AFP-logo-v02-noTagline-800.png';
        if (this.role === 'gallery') {
            this.homeLink = ['/ds-gallery-dashboard'];
            this.createLink = ['/galleries', 'gallery-product', 'create'];
            this.phonePrimary = ['Home', 'Inventory', 'Artists', 'Events', 'Inbox'];
            this.logoSrc = genericLogo;
        } else if (this.role === 'admin') {
            this.homeLink = ['/ds-admin-dashboard'];
            this.createLink = null;
            this.phonePrimary = ['Home', 'Users', 'Communication', 'Platform'];
            this.logoSrc = genericLogo;
        } else {
            this.homeLink = ['/ds-dashboard'];
            this.createLink = ['/artists', 'artworks', 'create'];
            this.phonePrimary = ['Home', 'Artworks', 'Website', 'Events', 'Messages'];
            this.logoSrc = 'assets/images/AFP-logo-horizontal/AFP-logo-pro-artists-300.png';
        }
    }

    private buildNav(role: ShellRole, user: string): NavItem[] {
        if (role === 'gallery') { return this.buildGalleryNav(); }
        if (role === 'admin') { return this.buildAdminNav(); }
        return this.buildArtistNav(user);
    }

    /**
     * Gallery rail — workflow-ordered for a busy gallery manager (Home · Inventory ·
     * Artists · Events · Selling · Website · Marketing · Inbox). Settings live in the
     * top bar. Wires existing gallery routes (Phase 1 = no routing changes).
     */
    private buildGalleryNav(): NavItem[] {
        return [
            { label: 'Home', icon: 'fa fa-home', link: ['/ds-gallery-dashboard'] },
            { label: 'Inventory', icon: 'fa fa-image', link: ['/galleries', 'gallery-products'], children: [
                { label: 'Consigned artworks', link: ['/galleries', 'gallery-products'] },
                { label: 'Gallery products', link: ['/galleries', 'view-gallery-products'] },
                { label: 'Requested products', link: ['/galleries', 'requested-products'] },
                { label: 'Labels', link: ['/labels-generation'] },
            ]},
            { label: 'Artists', icon: 'fa fa-handshake-o', link: ['/galleries', 'connected-artists'], children: [
                { label: 'Connected artists', link: ['/galleries', 'connected-artists'] },
                { label: 'Proposals', link: ['/galleries', 'view-proposals'] },
            ]},
            { label: 'Events', icon: 'fa fa-trophy', link: ['/events'], children: [
                { label: 'Your events', link: ['/events'] },
                { label: 'Create event', link: ['/events', 'create'] },
            ]},
            { label: 'Selling', icon: GALLERY_SELLING_HUB.icon, link: GALLERY_SELLING_HUB.link, match: hubMatchPrefixes(GALLERY_SELLING_HUB) },
            { label: 'Website', icon: GALLERY_WEBSITE_HUB.icon, link: GALLERY_WEBSITE_HUB.link, match: hubMatchPrefixes(GALLERY_WEBSITE_HUB) },
            { label: 'Marketing', icon: 'fa fa-bullhorn', link: SOCIAL_ACCOUNTS_HUB.link,
                match: [...hubMatchPrefixes(SOCIAL_ACCOUNTS_HUB), ...hubMatchPrefixes(LINK_SOCIALS_HUB)], children: [
                { label: 'Show Social Accounts', link: SOCIAL_ACCOUNTS_HUB.link },
                { label: 'Link Your Socials', link: LINK_SOCIALS_HUB.link },
            ]},
            { label: 'Inbox', icon: 'fa fa-comments', link: ['/inbox'] },
        ];
    }

    /** Admin rail — slim; mirrors the all-pages admin grouping. */
    private buildAdminNav(): NavItem[] {
        return [
            { label: 'Home', icon: 'fa fa-home', link: ['/ds-admin-dashboard'] },
            { label: 'Users', icon: 'fa fa-users', link: ['/admin', 'view'], children: [
                { label: 'Artists', link: ['/admin', 'view'] },
                { label: 'Galleries', link: ['/admin', 'view-galleries'] },
            ]},
            { label: 'Communication', icon: 'fa fa-envelope', link: ['/admin', 'email-events'], children: [
                { label: 'Email & SMS events', link: ['/admin', 'email-events'] },
                { label: 'Email templates', link: ['/admin', 'email-templates'] },
                { label: 'SMS templates', link: ['/admin', 'sms-templates'] },
                { label: 'Event workflow', link: ['/admin', 'system-events-workflow'] },
                { label: 'Email logs', link: ['/admin', 'email-track-log'] },
            ]},
            { label: 'Platform', icon: 'fa fa-cog', link: ['/admin', 'art-type'], children: [
                { label: 'Art types', link: ['/admin', 'art-type'] },
                { label: 'Application logs', link: ['/admin', 'application-log'] },
            ]},
            { label: 'Product setup', icon: 'fa fa-sliders', link: ['/artists', 'art-wotk-categories'], children: [
                { label: 'Sales categories', link: ['/artists', 'art-wotk-categories'] },
                { label: 'Product series', link: ['/artists', 'product-series'] },
                { label: 'Materials', link: ['/artists', 'product-material'] },
                { label: 'Features', link: ['/artists', 'product-glazes'] },
            ]},
        ];
    }

    /** Build the artist nav tree (mirrors all-pages buildArtist). */
    private buildArtistNav(user: string): NavItem[] {
        return [
            { label: 'Home', icon: 'fa fa-home', link: ['/ds-dashboard'] },
            { label: 'Artworks', icon: 'fa fa-paint-brush', link: ['/artists', 'artworks'], children: [
                { label: 'Your artworks', link: ['/artists', 'artworks'] },
                { label: 'Add an artwork', link: ['/artists', 'artworks', 'create'] },
                { label: 'Sales categories', link: ['/artists', 'art-wotk-categories'] },
                { label: 'Product series', link: ['/artists', 'product-series'] },
                { label: 'Materials', link: ['/artists', 'product-material'] },
                { label: 'Features', link: ['/artists', 'product-glazes'] },
            ]},
            // Website: a card hub (no drawer) — builder + public portfolio + external-platform links.
            { label: 'Website', icon: WEBSITE_HUB.icon, link: WEBSITE_HUB.link,
                match: ['/website', '/artists/portfolio', '/profile/' + user] },
            { label: 'Galleries', icon: 'fa fa-university', link: ['/artists', 'connected-galleries'], children: [
                { label: 'Connected galleries', link: ['/artists', 'connected-galleries'] },
                { label: 'Product requests', link: ['/artists', 'product-requests'] },
                { label: 'Proposals', link: ['/view-proposals'] },
            ]},
            { label: 'Events', icon: 'fa fa-trophy', link: ['/events', 'my-participated-events'], children: [
                { label: 'My events', link: ['/events', 'my-participated-events'] },
                { label: 'Available events', link: ['/events', 'available'] },
                { label: 'Create simple artwork', link: ['/events', 'create-artwork'] },
            ]},
            // Selling: a card hub (no drawer). Marketing: a drawer → two FB+IG card pages.
            { label: 'Selling', icon: SELLING_HUB.icon, link: SELLING_HUB.link, match: hubMatchPrefixes(SELLING_HUB) },
            { label: 'Marketing', icon: 'fa fa-bullhorn', link: SOCIAL_ACCOUNTS_HUB.link,
                match: [...hubMatchPrefixes(SOCIAL_ACCOUNTS_HUB), ...hubMatchPrefixes(LINK_SOCIALS_HUB)], children: [
                { label: 'Show Social Accounts', link: SOCIAL_ACCOUNTS_HUB.link },
                { label: 'Link Your Socials', link: LINK_SOCIALS_HUB.link },
            ]},
            // Inbox: a drawer collecting the artist's messages + the things that need a reply/action.
            { label: 'Inbox', icon: 'fa fa-comments', link: ['/inbox'], children: [
                { label: 'Messages', link: ['/inbox'] },
                { label: 'Connection requests', link: ['/connection-requests'] },
                // Its own area, not folded into Product requests: that heading reads as galleries
                // asking for stock, and a buyer asking about a piece is the highest-intent thing
                // that happens on an artist's site — it should not be hiding under someone else's word.
                { label: 'Enquiries', link: ['/artists', 'enquiries'] },
                { label: 'Product requests', link: ['/artists', 'product-requests'] },
                { label: 'Proposals', link: ['/view-proposals'] },
                { label: 'Dispatch', link: ['/dispatch'] },
            ]},
        ];
    }

    /** True when a rail item opens a secondary drawer (has sub-items). */
    public hasChildren(item: NavItem): boolean {
        return !!item.children && item.children.length > 0;
    }

    /**
     * Rail/bottom-bar click (Canva-style). Always loads the section's content AND opens
     * the secondary drawer for sectioned items (no caret needed — that's the default).
     * The drawer then stays open while you work within the section (see syncSectionToUrl).
     */
    public onRailClick(item: NavItem, e: Event): void {
        e.preventDefault();
        this.openSection = this.hasChildren(item) ? item : null;
        if (item.link) { this.router.navigate(item.link); }
    }

    public closeDrawer(): void {
        this.openSection = null;
    }

    /** A rail item is "active" when the current route lives under it. */
    public isSectionActive(item: NavItem): boolean {
        return this.sectionForUrl(this.router.url) === item;
    }

    /** Open the drawer for whichever section owns the given URL (keeps drawer in step with routing). */
    private syncSectionToUrl(url: string): void {
        const owner = this.sectionForUrl(url);
        // Only adjust when we positively recognise the route's owner: a sectioned owner opens
        // its drawer; a hub/direct owner closes it. On an UNRECOGNISED url (e.g. a redirect
        // target the matcher doesn't know) leave the drawer as the click set it — otherwise a
        // post-navigation close would make sectioned icons need a second click to stay open.
        if (owner) {
            this.openSection = this.hasChildren(owner) ? owner : null;
        }
    }

    /** Find the rail item whose default route or any child route best matches the URL. */
    private sectionForUrl(url: string): NavItem | null {
        const path = (url || '').split('?')[0];
        let best: NavItem | null = null;
        let bestLen = -1;
        for (const item of this.navItems) {
            const prefixes: string[] = [];
            if (item.link) { prefixes.push('/' + item.link.filter(s => typeof s === 'string').join('/')); }
            (item.children || []).forEach(c => prefixes.push('/' + c.link.filter(s => typeof s === 'string').join('/')));
            (item.match || []).forEach(p => prefixes.push(p));
            for (const prefix of prefixes) {
                if ((path === prefix || path.startsWith(prefix + '/')) && prefix.length > bestLen) {
                    best = item;
                    bestLen = prefix.length;
                }
            }
        }
        return best;
    }

    public isPrimary(label: string): boolean {
        return this.phonePrimary.includes(label);
    }

    public initials(): string {
        return (this.firstName || 'A').trim().charAt(0).toUpperCase();
    }

    public toggleUserMenu(e?: Event): void {
        if (e) { e.stopPropagation(); }
        this.isUserMenuOpen = !this.isUserMenuOpen;
    }
    public closeUserMenu(): void {
        this.isUserMenuOpen = false;
    }

    public logout(e: Event): void {
        e.preventDefault();
        this.closeUserMenu();
        this.authService.logout().subscribe(() => window.location.reload());
    }

    /** Admin impersonation exit — return to the admin account (mirrors the old shell's "Back to Admin"). */
    public exitToAdmin(e: Event): void {
        e.preventDefault();
        this.spinner.show();
        this.authService.exitAccount().subscribe({
            next: (res: ResponseObject) => {
                this.spinner.hide();
                if (res?.Success) {
                    this.router.navigate(['/ds-admin-dashboard']).then(() => window.location.reload());
                }
            },
            error: () => { this.spinner.hide(); }
        });
    }
}
